import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { BaseButton, IconContainer, Loader } from './style';
import { ThemeContext } from 'helpers/contexts.js';
import { COLOR, VARIENT, SIZE, POSITION, ICON_ANIMATION_TYPE } from 'constant';

Button.propTypes = {
  text: PropTypes.string.isRequired,
  color: PropTypes.oneOf([
    COLOR.primary,
    COLOR.secondary,
    COLOR.danger,
    COLOR.invert,
    COLOR.light,
    COLOR.white,
    COLOR.blue,
    COLOR.orange,
    COLOR.dark,
  ]),
  variant: PropTypes.oneOf([VARIENT.contained, VARIENT.outlined]),
  size: PropTypes.oneOf([
    SIZE.xs,
    SIZE.small,
    SIZE.medium,
    SIZE.large,
    SIZE.xl,
    SIZE.xxl,
  ]),
  rounded: PropTypes.bool,
  disabled: PropTypes.bool,
  isActive: PropTypes.bool,
  onClick: PropTypes.func,
  iconName: PropTypes.string,
  iconPosition: PropTypes.oneOf([POSITION.end, POSITION.start]),
  iconAnimationType: PropTypes.oneOf(Object.values(ICON_ANIMATION_TYPE)),
  width: PropTypes.string,
  id: PropTypes.string,
  dataTestId: PropTypes.string,
};

Button.defaultProps = {
  iconPosition: POSITION.end,
  color: COLOR.primary,
  variant: VARIENT.contained,
  size: SIZE.small,
  onClick: null,
  disabled: false,
  rounded: false,
  isActive: false,
  iconName: null,
  dataTestId: 'button',
};

function Button(props) {
  const theme = useContext(ThemeContext);
  let stateStyle = getStateStyle(theme.colors)[props.variant][props.color];
  let borderRadius = props.rounded ? '50px' : '4px';
  let cursor = props.disabled ? 'not-allowed' : 'pointer';
  let paddingBlock;
  let paddingInLine;
  let fontSize;
  let lineHeight;
  let iconMarginInline;
  let iconMarginBottom;

  function getStyle() {
    if (props.disabled) {
      return stateStyle.disabled;
    }
    if (props.isActive) {
      return stateStyle.active;
    }
    return stateStyle.default;
  }

  switch (props.size) {
    case SIZE.xs:
      paddingBlock = 1;
      paddingInLine = 8;
      fontSize = 12;
      iconMarginInline = 4;
      iconMarginBottom = 0;
      lineHeight = 16;
      break;
    case SIZE.small:
      paddingBlock = 1;
      paddingInLine = 12;
      fontSize = 14;
      iconMarginInline = 8;
      iconMarginBottom = 0;
      lineHeight = 20;
      break;
    case SIZE.medium:
      paddingBlock = 3;
      paddingInLine = 12;
      fontSize = 15;
      iconMarginInline = 8;
      iconMarginBottom = 0;
      lineHeight = 20;
      break;
    case SIZE.large:
      paddingBlock = 3;
      paddingInLine = 16;
      fontSize = 16;
      iconMarginInline = 8;
      iconMarginBottom = 0;
      lineHeight = 24;
      break;
    case SIZE.xl:
      paddingBlock = 7;
      paddingInLine = 16;
      fontSize = 18;
      iconMarginInline = 8;
      iconMarginBottom = 0;
      lineHeight = 24;
      break;
    case SIZE.xxl:
      paddingBlock = 11;
      paddingInLine = 20;
      fontSize = 20;
      iconMarginInline = 8;
      iconMarginBottom = 0;
      lineHeight = 24;
      break;
    default:
  }
  return (
    <BaseButton
      type='submit'
      id={props.id}
      onClick={!props.disabled && props.onClick}
      cursor={cursor}
      borderRadius={borderRadius}
      baseStyle={getStyle}
      paddingBlock={paddingBlock}
      paddingInLine={paddingInLine}
      fontSize={fontSize}
      lineHeight={`${lineHeight}px`}
      flexDirection={
        props.iconPosition === POSITION.end ? 'row' : 'row-reverse'
      }
      hoverStyle={props.disabled || props.isActive ? '' : stateStyle.hover}
      activeStyle={props.disabled ? '' : stateStyle.active}
      display={props.iconName ? 'flex' : ''}
      width={props.width}
      data-testid={props.dataTestId}
    >
      {props.text}
      {props.isLoading ? (
        <IconContainer
          isShowIcon={props.isLoading}
          marginInlineEnd={
            props.iconPosition === POSITION.end ? '0' : `${iconMarginInline}px`
          }
          marginInlineStart={
            props.iconPosition === POSITION.end ? `${iconMarginInline}px` : '0'
          }
          marginBottom={`${iconMarginBottom}px`}
        >
          {/* <Icon
            name={props.iconName}
            size={props.size}
            animationType={props.iconAnimationType}
          ></Icon> */}
          <Loader />
        </IconContainer>
      ) : (
        <></>
      )}
    </BaseButton>
  );
}

export default React.memo(Button);

const getStateStyle = (colors) => {
  return {
    contained: {
      primary: {
        default: `color: #fff;
      background-color: ${colors.green.shamrock};
      border:1px solid ${colors.green.shamrock};
    `,
        hover: `color: #fff;
      background-color: ${colors.green.jungleGreen};
      border:1px solid ${colors.green.jungleGreen};
    `,
        active: `color: #fff;
      background-color: ${colors.green.eucalyptus};
      border:1px solid ${colors.green.eucalyptus};
    `,
        disabled: `color:#fff;
      background-color: ${colors.green.algaeGreen};
      border:1px solid ${colors.green.algaeGreen};
      
    `,
      },
      secondary: {
        default: `color: #fff;
      background-color: ${colors.gray.aluminium};
      border:1px solid ${colors.gray.aluminium};
    `,
        hover: `color: #fff;
      background-color: ${colors.gray.gray600};
      border:1px solid ${colors.gray.gray600};
      `,
        active: `color: #fff;
      background-color: ${colors.gray.midGray};
      border:1px solid ${colors.gray.midGray};
      `,
        disabled: `color: #fff;
      background-color: ${colors.gray.iron};
      border:1px solid ${colors.gray.iron};
      
      `,
      },
      danger: {
        default: `color: #fff;
      background-color: ${colors.red.flamingo};
      border:1px solid ${colors.red.flamingo};
    `,
        hover: `color: #fff;
      background-color:${colors.red.mojo};
      border:1px solid ${colors.red.mojo}};
    `,
        active: `color: #fff;
      background-color:${colors.red.stiletto};
      border:1px solid ${colors.red.stiletto};
    `,
        disabled: `color: #fff;
      background-color:${colors.red.stiletto};
      border:1px solid ${colors.red.stiletto};
      
    `,
      },
      invert: {
        default: `color: ${colors.gray.charcoal};
    background-color: ${colors.gray.gray200};
    border:1px solid ${colors.gray.gray200};
  `,
        hover: `color: ${colors.gray.charcoal};
    background-color:${colors.gray.gray100};
    border:1px solid ${colors.gray.gray100};
  `,
        active: `color: ${colors.gray.charcoal};
    background-color:${colors.gray.gray100};
    border:1px solid ${colors.gray.gray100};
  `,
        disabled: `color: ${colors.gray.charcoal};
    background-color: ${colors.gray.jumbo};
    border:1px solid ${colors.gray.jumbo};
    
  `,
      },
      light: {
        default: `color: ${colors.gray.charcoal};
  background-color: ${colors.gray.gray200};
  border:1px solid ${colors.gray.gray200};
`,
        hover: `color: ${colors.white};
  background-color:${colors.gray.gray600};
  border:1px solid ${colors.gray.gray600};
`,
        active: `color: ${colors.white};
  background-color:${colors.gray.iron};
  border:1px solid ${colors.gray.iron};
`,
        disabled: `color: #fff;
  background-color: ${colors.gray.iron};
  border:1px solid ${colors.gray.iron};
  
`,
      },
      white: {
        default: `color: ${colors.gray.charcoal};
  background-color: ${colors.white};
  border:1px solid ${colors.white};
`,
        hover: `color: ${colors.gray.charcoal};
  background-color:${colors.gray.gray300};
  border:1px solid ${colors.gray.gray300};
`,
        active: `color: ${colors.gray.charcoal};
  background-color:${colors.gray.gray300};
  border:1px solid ${colors.gray.gray300};
`,
        disabled: `color: ${colors.gray.charcoal};
  background-color: ${colors.gray.gray300};
  border:1px solid ${colors.gray.gray300};
  
`,
      },
      blue: {
        default: `color: ${colors.white};
  background-color: ${colors.blue.royalBlue};
  border:1px solid ${colors.blue.royalBlue};
`,
        hover: `color:  ${colors.white};
  background-color:#3462C6;
  border:1px solid #3462C6;
`,
        active: `color:${colors.white};
  background-color: #274B99;
  border:1px solid  #274B99;
`,
        disabled: `color: ${colors.white};
  background-color: ${colors.blue.royalBlue};
  border:1px solid${colors.blue.royalBlue};
  opacity: 0.3; 
  
`,
      },
      orange: {
        default: `color: ${colors.white};
  background-color: ${colors.orange.vividTangerine};
  border:1px solid ${colors.orange.vividTangerine};
`,
        hover: `color: ${colors.white};
  background-color:${colors.orange.froly};
  border:1px solid${colors.orange.froly};
`,
        active: `color: ${colors.white};
  background-color: ${colors.orange.sunglo};
  border:1px solid ${colors.orange.sunglo};
  opacity: 0.8;
`,
      },
      dark: {
        default: `color: ${colors.white};
                  background-color: ${colors.black};
                  border:1px solid ${colors.black};
`,
        hover: `color: ${colors.white};
                  background-color:${colors.black};
                  border:1px solid${colors.black};
                  opacity: 0.8;
`,
        active: `color: ${colors.white};
                  background-color: ${colors.black};
                  border:1px solid ${colors.black};
                  opacity: 0.6;
`,
      },
    },
    outlined: {
      primary: {
        default: `color: ${colors.green.shamrock};
    background-color: transparent;
    border:1px solid ${colors.green.shamrock};
  `,
        hover: `color:#fff;
    background-color: ${colors.green.shamrock};
    border:1px solid ${colors.green.shamrock};
  `,
        active: `color: #fff
    background-color: ${colors.green.jungleGreen};
    border:1px solid ${colors.green.jungleGreen};
  `,
        disabled: `color:  #fff;
    background-color: ${colors.green.algaeGreen};
    border:1px solid ${colors.green.algaeGreen};
    
  `,
      },
      secondary: {
        default: `color: ${colors.gray.gray600};
    background-color: transparent;
    border:1px solid ${colors.gray.aluminium};
  `,
        hover: `color: #fff;
    background-color: ${colors.gray.aluminium};
    border:1px solid ${colors.gray.aluminium};
    `,
        active: `color: #fff;
    background-color: ${colors.gray.midGray};
    border:1px solid ${colors.gray.midGray};
    `,
        disabled: `color: #fff;
    background-color: ${colors.gray.iron};
    border:1px solid ${colors.gray.iron};
    
    `,
      },
      danger: {
        default: `color: ${colors.red.flamingo};
    background-color: transparent;
    border:1px solid ${colors.red.flamingo};
  `,
        hover: `color: #fff;
    background-color:${colors.red.flamingo};
    border:1px solid ${colors.red.flamingo};
  `,
        active: `color: #fff;
    background-color:${colors.red.stiletto};
    border:1px solid ${colors.red.stiletto};
  `,
        disabled: `color: #fff;
    background-color: ${colors.red.stiletto};
    border:1px solid ${colors.red.stiletto};
    
  `,
      },
      invert: {
        default: `color: ${colors.gray.gray500};
  background-color: transparent;
  border:1px solid ${colors.gray.gray500};
`,
        hover: `color: ${colors.gray.charcoal};
  background-color:${colors.gray.iron};
  border:1px solid ${colors.gray.iron};
`,
        active: `color: ${colors.gray.charcoal};
  background-color:${colors.gray.gray200};
  border:1px solid ${colors.gray.gray200};
`,
        disabled: `color: ${colors.gray.charcoal};
  background-color: ${colors.gray.jumbo};
  border:1px solid ${colors.gray.jumbo};
  
`,
      },
      light: {
        default: `color: ${colors.gray.gray600};
background-color: transparent;
border:1px solid ${colors.gray.gray400};
`,
        hover: `color: ${colors.gray.charcoal};
background-color:${colors.gray.gray300};
border:1px solid ${colors.gray.gray300};
`,
        active: `color: ${colors.gray.charcoal};
background-color:${colors.gray.gray400};
border:1px solid ${colors.gray.gray400};
`,
        disabled: `color: #fff;
background-color: ${colors.gray.iron};
border:1px solid ${colors.gray.iron};

`,
      },
      white: {
        default: `color: ${colors.white};
  background-color: transparent;
  border:1px solid ${colors.white};
`,
        hover: `color: ${colors.gray.charcoal};
  background-color:${colors.white};
  border:1px solid ${colors.white};
`,
        active: `color: ${colors.gray.charcoal};
  background-color:${colors.white};
  border:1px solid ${colors.white};
`,
        disabled: `color: ${colors.gray.gray400};
  background-color: ${colors.gray.gray200};
  border:1px solid ${colors.gray.gray200};
  
`,
      },
      blue: {
        default: `color: ${colors.blue.royalBlue};
      background-color: transparent;
      border:1px solid ${colors.blue.royalBlue};
      `,
        hover: `color:${colors.white};
      background-color: #3462C6;
      border:1px solid: #3462C6;
      `,
        active: `color: ${colors.white}
      background-color: #274B99;
      border:1px solid #274B99;
      `,
        disabled: `color:  ${colors.white};
        background-color: ${colors.blue.royalBlue};
        border:1px solid${colors.blue.royalBlue};
        opacity: 0.3;
      `,
      },
    },
  };
};
